import {MouseEventHandler, useContext, useEffect, useRef, useState,} from "react";
import {GenericComponentModel, SelectElementModelBE, SelectElementModelFE,} from "../../../api/types/generic";
import {useRecoilState} from "recoil";
import {useTranslation} from "react-i18next";
import SearchFilters from "../../../common/components/SearchFilters";
import {commercialSegmentListAtom} from "../../../state/commercialSegmentsAtom";
import {CommercialCategoryEntityResponse, CommercialCategorySelectFE,} from "../../../api/types/commercialCategory";
import {fromGenericBeToGenericFe} from "../../../utils";
import {useCommercialCategoryRepository} from "../../../api/repositories/CommercialCategoryRepository";
import {useCommercialSegmentRepository} from "../../../api/repositories/CommercialSegmentRepository";
import {useQuery} from "@tanstack/react-query";
import {useRentalPeriodRepository} from "../../../api/repositories/RentalPeriodRepository";
import {GlobalContext} from "../../../../../util/state/GlobalContextProvider";

const RateFilters = ({
                         handleClickResetSearch,
                         handleGetRateList,
                         testFilters,
                         setTestFilters
                     }: {
    handleGetRateList: MouseEventHandler<HTMLButtonElement>
    handleClickResetSearch: MouseEventHandler<HTMLButtonElement>
    testFilters: any
    setTestFilters: any
}) => {
    // Use Recoil - solo per segment (non viene sovrascritto da altri componenti)
    const [commercialSegmentList, setCommercialSegmentList] = useRecoilState(commercialSegmentListAtom);

    // Use State LOCALE per le categorie (evita conflitti con altri componenti)
    const [localCommercialCategoryList, setLocalCommercialCategoryList] = useState<CommercialCategorySelectFE[]>([]);

    // Use Translation
    const {t} = useTranslation(["translation", "pricing"]);

    // Use State
    const [tpUnitList, setTpUnitsList] = useState<string[]>([]);
    const [legalEntityError, setLegalEntityError] = useState<string>("");
    const [categoriesLoaded, setCategoriesLoaded] = useState<boolean>(false);
    
    // Use Ref per mantenere le categorie originali senza causare re-render
    const commercialCategoryListOriginalRef = useRef<CommercialCategorySelectFE[]>([]);

    // Use Repository Hooks
    const {getCommercialCategories} = useCommercialCategoryRepository();
    const {getCommercialSegments} = useCommercialSegmentRepository();
    const {getTpUnitsEnumNoAbsolute} = useRentalPeriodRepository();

    // Use Context - spostato prima di useQuery per avere accesso a user
    const {
        globalData: {user}
    } = useContext(GlobalContext);

    // Use Query
    useQuery<CommercialCategoryEntityResponse[], Error>({
        queryKey: ["PRICING_COMMERCIAL_CATEGORIES"],
        queryFn: () => getCommercialCategories(),
        onSuccess: (commercialCategoriesData) => {
            const commercialCategoryList: CommercialCategorySelectFE[] =
                fromGenericBeToGenericFe(commercialCategoriesData);
            commercialCategoryListOriginalRef.current = commercialCategoryList;
            setCategoriesLoaded(true);
        },
    });
    useQuery<SelectElementModelBE[], Error>({
        queryKey: ["PRICING_COMMERCIAL_SEGMENTS"],
        queryFn: () => getCommercialSegments(),
        onSuccess: (commercialSegmentsData) => {
            const commercialSegmentList: SelectElementModelFE[] =
                fromGenericBeToGenericFe(commercialSegmentsData);
            setCommercialSegmentList(commercialSegmentList);
        },
    });
    useQuery<string[], Error>({
        queryKey: ["PRICING_TP_UNIT"],
        queryFn: () => getTpUnitsEnumNoAbsolute(),
        onSuccess: (tpUnitsData) => {
            setTpUnitsList(tpUnitsData);
        },
    });

    const legalEntityOptions: SelectElementModelFE[] = user.legalEntities?.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:legalEntities.'.concat(el.name))
        })
    );

    const dealerOptions: SelectElementModelFE[] = user.dealers.map(
        (el: SelectElementModelBE) => ({
            code: el.name,
            label: t('translation:dealers.'.concat(el.name))
        })
    );

    // Funzione per applicare i filtri alle categorie
    const applyFilters = (
        categoriesToFilter: CommercialCategorySelectFE[],
        segmentFilter: SelectElementModelFE | null,
        legalEntitiesFilter: SelectElementModelFE[] | null
    ) => {
        if (categoriesToFilter.length === 0) return;
        
        let filtered = [...categoriesToFilter];
        
        // 1. Prima filtra per segment se selezionato
        if (segmentFilter) {
            filtered = filtered.filter(
                (cc) => cc.commercialSegment.id === segmentFilter.code
            );
        }
        
        // 2. Poi applica il filtro per legal entities e "esplodi" le categorie
        if (legalEntitiesFilter && Array.isArray(legalEntitiesFilter) && legalEntitiesFilter.length > 0) {
            const selectedLegalEntityCodes = legalEntitiesFilter.map((le: SelectElementModelFE) => le.code);
            
            // Filtra le categorie che hanno almeno un label per le legal entities selezionate
            const filteredCategories = filtered.filter(category => 
                category.labels && selectedLegalEntityCodes.some((leCode: string | number) => 
                    category.labels?.some(label => label.country === leCode)
                )
            );
            
            // "Esplodi" le categorie: crea una voce separata per ogni label unico
            const expandedCategories: CommercialCategorySelectFE[] = [];
            
            filteredCategories.forEach(category => {
                if (category.labels && category.labels.length > 0) {
                    const relevantLabels = category.labels.filter(label => 
                        selectedLegalEntityCodes.includes(label.country)
                    );
                    
                    const uniqueLabelsMap = new Map<string, typeof relevantLabels>();
                    relevantLabels.forEach(labelObj => {
                        if (!uniqueLabelsMap.has(labelObj.label)) {
                            uniqueLabelsMap.set(labelObj.label, [labelObj]);
                        } else {
                            uniqueLabelsMap.get(labelObj.label)!.push(labelObj);
                        }
                    });
                    
                    uniqueLabelsMap.forEach((labelGroup, uniqueLabel) => {
                        expandedCategories.push({
                            ...category,
                            label: uniqueLabel,
                            labels: labelGroup
                        });
                    });
                } else {
                    expandedCategories.push(category);
                }
            });
            
            setLocalCommercialCategoryList(expandedCategories);
        } else {
            // Nessuna legal entity selezionata: mostra tutte le categorie deduplicate per label
            const uniqueLabelsMap = new Map<string, CommercialCategorySelectFE>();
            filtered.forEach(category => {
                if (!uniqueLabelsMap.has(category.label)) {
                    uniqueLabelsMap.set(category.label, category);
                }
            });
            setLocalCommercialCategoryList(Array.from(uniqueLabelsMap.values()));
        }
    };

    // Effect per applicare i filtri quando cambiano i dati o i filtri
    useEffect(() => {
        // Non eseguire se i dati non sono ancora caricati
        if (!categoriesLoaded || commercialCategoryListOriginalRef.current.length === 0) {
            return;
        }
        
        // Fallback mono-tenant: se nessuna legal entity selezionata, usa il tenant dell'utente
        let legalEntitiesForFilter = testFilters.legalEntities as SelectElementModelFE[] | null;
        if ((!legalEntitiesForFilter || !Array.isArray(legalEntitiesForFilter) || legalEntitiesForFilter.length === 0)
            && user.legalEntities?.length === 1) {
            legalEntitiesForFilter = [{ code: user.legalEntities[0].name, label: user.legalEntities[0].name }];
        }
        
        applyFilters(
            commercialCategoryListOriginalRef.current,
            testFilters.commercialSegmentId as SelectElementModelFE | null,
            legalEntitiesForFilter
        );
    }, [testFilters.commercialSegmentId, testFilters.legalEntities, categoriesLoaded]);

    // Validation function for mandatory legal entity
    const validateLegalEntity = (): boolean => {
        if (!testFilters.legalEntities || !Array.isArray(testFilters.legalEntities) || testFilters.legalEntities.length === 0) {
            setLegalEntityError(t("pricing:validation.legalEntityRequired", "Select at least one option"));
            return false;
        }
        setLegalEntityError("");
        return true;
    };

    // Effect per validare legal entities quando cambiano
    useEffect(() => {
        if (!testFilters.legalEntities || !Array.isArray(testFilters.legalEntities) || testFilters.legalEntities.length === 0) {
            setLegalEntityError(t("pricing:validation.legalEntityRequired", "Select at least one option"));
        } else {
            setLegalEntityError("");
        }
    }, [testFilters.legalEntities]);

    // Enhanced search handler with validation
    const handleValidatedSearch: MouseEventHandler<HTMLButtonElement> = (e) => {
        if (!validateLegalEntity()) {
            return;
        }
        handleGetRateList(e);
    };

    const filtersOutOfCollapse: GenericComponentModel[] = [
        {
            name: "name",
            value: testFilters.name as string,
            label: `${t("pricing:rate.form.labels.mainDefinitionLabels.name")}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, name: value}),
            options: [],
            type: "TextField",
        },
        {
            name: "commercialSegment",
            value: testFilters.commercialSegmentId as SelectElementModelFE,
            label: `${t(
                "pricing:rate.form.labels.mainDefinitionLabels.commercialSegment"
            )}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({
                    ...testFilters,
                    commercialSegmentId: value,
                    commercialCategoryId: null,
                }),
            options: commercialSegmentList,
            type: "Autocomplete",
        },
        {
            name: "commercialCategoryId",
            value: testFilters.commercialCategoryId as CommercialCategorySelectFE,
            label: `${t(
                "pricing:rate.form.labels.mainDefinitionLabels.commercialCategory"
            )}`,
            handleChange: (value: CommercialCategorySelectFE) =>
                setTestFilters({
                    ...testFilters,
                    commercialCategoryId: value,
                }),
            options: localCommercialCategoryList,
            type: "Autocomplete",
            disabled: !testFilters.legalEntities || !Array.isArray(testFilters.legalEntities) || testFilters.legalEntities.length === 0
        },
    ];
    const filtersInCollapse: GenericComponentModel[] = [
        {
            name: "reservationFrom",
            value: testFilters.reservationFrom as string,
            options: [],
            label: `${t(
                "pricing:rate.form.labels.mainDefinitionLabels.reservationFrom"
            )}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, reservationFrom: value}),
            type: "Date",
        },
        {
            name: "reservationTo",
            value: testFilters.reservationTo as string,
            options: [],
            label: `${t(
                "pricing:rate.form.labels.mainDefinitionLabels.reservationTo"
            )}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, reservationTo: value}),
            type: "Date",
        },
        {
            name: "pickupDateFrom",
            value: testFilters.pickupDateFrom as string,
            options: [],
            label: `${t(
                "pricing:rate.form.labels.mainDefinitionLabels.pickupDateFrom"
            )}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, pickupDateFrom: value}),
            type: "Date",
        },
        {
            name: "pickupDateTo",
            value: testFilters.pickupDateTo as string,
            options: [],
            label: `${t(
                "pricing:rate.form.labels.mainDefinitionLabels.pickupDateTo"
            )}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, pickupDateTo: value}),
            type: "Date",
        },
        {
            name: "tpUnit",
            value: testFilters.tpUnit as string,
            label: `${t("pricing:rate.form.labels.mainDefinitionLabels.unit")}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, tpUnit: value}),
            options: tpUnitList,
            type: "Autocomplete",
        },
        {
            name: "id",
            value: testFilters.id as string,
            label: `${t("pricing:rate.form.labels.mainDefinitionLabels.id")}`,
            handleChange: (value: string) =>
                setTestFilters({...testFilters, id: value}),
            options: [],
            type: "TextField",
        },
        {
            name: "legalEntities",
            value: testFilters.legalEntities as any, // Cast per supportare array
            label: `${t("pricing:rate.form.labels.mainDefinitionLabels.legalEntity")}*`,
            handleChange: (value: SelectElementModelFE[]) =>
                setTestFilters({
                    ...testFilters, 
                    legalEntities: value || [],
                    commercialCategoryId: null // Reset dependent fields
                }),
            options: legalEntityOptions || [],
            type: "AutocompleteM",
            error: !!legalEntityError,
            helperText: legalEntityError,
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        },
        {
            name: "dealers",
            value: testFilters.dealers as SelectElementModelFE,
            label: `${t("pricing:rate.form.labels.mainDefinitionLabels.dealer")}`,
            handleChange: (value: SelectElementModelFE) =>
                setTestFilters({...testFilters, dealers: value}),
            options: dealerOptions || [],
            type: "AutocompleteM",
            hide: user.legalEntities.length === 1 && user.dealers.length === 1 ? true : false
        }
    ];

    return (
        <SearchFilters
            filtersOutOfCollapse={filtersOutOfCollapse}
            filtersInCollapse={filtersInCollapse}
            handleClickSearch={handleValidatedSearch}
            handleClickResetSearch={handleClickResetSearch}
        />
    );
};

export default RateFilters;